<template>
  <div class="dashboard tw:drawer" :class="{ 'tw:drawer-open': sideBar.pinned }">
    <input id="drawer-toggle" type="checkbox" class="tw:drawer-toggle" />
    <div class="tw:drawer-content tw:flex tw:flex-col">
      <Skeleton v-if="loading" shape="rectangle" width="100%" height="200px" class="tw:rounded" />

      <div class="inner" v-if="!loading">
        <div class="tw:flex tw:flex-col tw:justify-between tw:gap-3 tw:lg:flex-row">
          <div class="tw:flex tw:flex-row tw:items-center">
            <label
              v-if="!sideBar.pinned"
              for="drawer-toggle"
              class="drawer-button tw:w-fit-content tw:btn tw:relative tw:mr-3 tw:drop-shadow-sm tw:btn-xs"
              :class="{ 'tw:hidden': allDocuments.length === 0 }">
              <ListFilterIcon :size="16" />
              <div
                class="tw:absolute tw:-top-2 tw:-right-3 tw:badge tw:border-red-200 tw:bg-red-200 tw:badge-sm"
                v-if="appliedFilterCount > 0">
                {{ appliedFilterCount }}
              </div>
            </label>
            <h2>Documents</h2>

            <template v-if="!isClientRole">
              <template v-if="selectionMode">
                <AppButton class="tw:ml-3" @click="cancelSelection">Cancel</AppButton>
                <AppButton primary class="tw:ml-3" @click="saveSelection">Save</AppButton>
                <Spinner v-if="isSavingPublishStatus" size="sm" class="tw:ml-3 tw:align-middle" />
              </template>

              <template v-else>
                <AppButton class="tw:ml-3" @click="toggleSelectionMode">Publish</AppButton>
                <Tooltip text="Publishing will allow clients to view selected documents" position="top">
                  <Info :size="18" class="tw:ml-2 tw:text-gray-400" />
                </Tooltip>
              </template>
            </template>
          </div>

          <div class="tw:flex tw:flex-col tw:gap-3 tw:md:flex-row tw:md:items-center">
            <label class="tw:input tw:input-sm tw:flex tw:min-w-96 tw:items-center tw:gap-2 tw:place-self-end tw:self-center">
              <input type="text" placeholder="Search" v-model="searchFilter" class="tw:grow" />
              <XIcon v-if="searchFilter" :size="20" @click="searchFilter = ''" />
            </label>

            <toggle id="show-current-only" v-model="showCurrentOnly" label="Show Current Only" class="tw:self-center" />

            <!-- restrict document upload by permission for only client users -->

            <RouterLink
              v-if="uploadEnabled && (user.hasPermissions(UserPermission.DocumentsWrite) || isBrokerRole)"
              :to="`/account/${accountId}/documents/upload`"
              class="tw:btn tw:btn-sm tw:btn-primary tw:rounded-full">
              Upload Documents
            </RouterLink>
          </div>
        </div>

        <div v-if="(documents || []).length === 0" class="tw:mt-16 tw:flex tw:justify-center">
          <h2 class="tw:text-2xl tw:text-gray-500">No documents found</h2>
        </div>

        <div v-if="documents && documents.length > 0">
          <div class="table-hscroller">
            <table id="documents-table" v-tablesort class="dense mt-4">
              <thead>
                <tr>
                  <td class="tw:rounded-tl-md tw:rounded-bl-md">Year</td>
                  <td>Type</td>
                  <td v-if="!isClientRole" class="nosort text-right">
                    <template v-if="selectionMode">
                      <input type="checkbox" v-model="selectAll" @click.stop @change="toggleSelectAll" />
                    </template>
                    <template v-else> &nbsp; </template>
                  </td>
                  <td>File name</td>
                  <td v-if="isParentAccount">Account Name</td>
                  <td>Policy Number</td>
                  <td>Carrier</td>
                  <td>Coverage Type</td>
                  <td>Tags</td>
                  <td class="dataindex text-right">Uploaded</td>
                  <td class="actions tw:rounded-tr-md tw:rounded-br-md">&nbsp;</td>
                </tr>
              </thead>

              <tbody>
                <ContextMenuRoot
                  v-for="(document, index) in documents"
                  :key="index">
                  <ContextMenuTrigger as-child>
                    <tr @contextmenu="setSelectedDocument(document)">
                      <td>{{ document.policyEffectiveYear || '' }}</td>
                      <td>{{ document.documentTypeName }}</td>
                      <td
                        v-if="!isClientRole"
                        class="text-right">
                        <template v-if="selectionMode && document.documentDomainName">
                          <input
                            v-if="document.documentDomainName === DocumentDomainTypeClientUploaded"
                            type="checkbox"
                            checked
                            disabled />
                          <input
                            v-else
                            type="checkbox"
                            v-model="document.selected" />
                        </template>

                        <template v-else>
                          <div
                            v-if="document.published"
                            data-tip="This document is published and viewable by client users"
                            class="tw:tooltip tw:tooltip-top tw:top-0.5 tw:before:max-w-sm">
                            <Users
                              class="tw:text-gray-500"
                              :size="16" />
                          </div>
                          <div
                            v-else-if="document.documentDomainName === DocumentDomainTypeClientUploaded"
                            data-tip="This document was uploaded by a client"
                            class="tw:tooltip tw:tooltip-top tw:top-0.5 tw:before:max-w-sm">
                            <Users
                              class="tw:text-gray-500"
                              :size="16" />
                          </div>
                        </template>
                      </td>
                      <td :title="document.displayName ?? document.fileName">
                        <a
                          @click="isDocumentTypeViewable(document) ? openDocumentModal(document) : downloadFile(document)"
                          class="tw:hover:cursor-pointer">
                          <span v-html="filters.highlight(document.displayName ?? document.s3Name, searchText)" />
                        </a>
                      </td>
                      <td v-if="isParentAccount">{{ document.accountName }}</td>
                      <td>
                        <div v-html="filters.highlight(document.mainPolicyNumber, searchText)"></div>
                        <div
                          class="tw:text-xs"
                          v-html="filters.highlight(document.policyDescription!, searchText)"></div>
                      </td>
                      <td>{{ document.carrierName }}</td>
                      <td>{{ document.coverageType }}</td>
                      <td>
                        <div
                          class="chips tw:m-0 tw:flex tw:flex-wrap tw:gap-1"
                          v-if="document.tags && document.tags.length > 0">
                          <Badge
                            v-for="tag in document.tags"
                            :key="document.tags.indexOf(tag)"
                            variant="outline"
                            class="tw:border-gray-300 tw:bg-none tw:font-normal tw:text-primary">
                            <div v-html="filters.highlight(tag, searchText)"></div>
                          </Badge>
                        </div>
                      </td>
                      <td
                        class="dataindex text-right no-snapshot tw:cursor-pointer tw:text-xs"
                        :data-index="index"
                        @click="showRelativeTime = !showRelativeTime">
                        <div v-if="showRelativeTime">{{ relativeDateTime(document.createdByDate) }}</div>
                        <div v-else>
                          {{ toDateTime(document.createdByDate ?? '').toLocaleString(DateTime.DATETIME_FULL) }}
                        </div>
                        <div v-if="!isClientRole">{{ formatCreatedByUser(document.createdByUser) }}</div>
                      </td>

                      <td class="actions">
                        <MenuDropdown :items="getDocumentMenuItems(document)" size="md" left>
                          <template #action>
                            <label tabindex="0" class="tw:btn tw:btn-ghost tw:btn-sm tw:btn-square">
                              <EllipsisVertical :size="20" />
                            </label>
                          </template>
                        </MenuDropdown>
                      </td>
                    </tr>
                  </ContextMenuTrigger>
                  <ContextMenuPortal to="body">
                    <ContextMenuContent
                      class="tw:max-w-80 tw:min-w-60 tw:gap-1 tw:rounded-md tw:bg-white tw:p-2 tw:text-sm tw:outline-1 tw:outline-gray-300 tw:drop-shadow-xl"
                      @interact-outside="outsideClicked = true">
                      <!-- View Document -->
                      <ContextMenuItem
                        class="tw:flex tw:h-6 tw:max-w-60 tw:items-center tw:rounded-sm tw:p-4 tw:outline-none tw:select-none tw:hover:bg-gray-200"
                        @click="viewSelectedDocument">
                        View Document
                      </ContextMenuItem>

                      <!-- Download Document -->
                      <ContextMenuItem
                        class="tw:flex tw:h-6 tw:max-w-60 tw:items-center tw:rounded-sm tw:p-4 tw:outline-none tw:select-none tw:hover:bg-gray-200"
                        @click="downloadSelectedDocument">
                        Download Document
                      </ContextMenuItem>

                      <!-- Edit Document -->
                      <ContextMenuItem
                        class="tw:flex tw:h-6 tw:max-w-60 tw:items-center tw:rounded-sm tw:p-4 tw:outline-none tw:select-none tw:hover:bg-gray-200"
                        @click="openDocumentEditModal(selectedDocument)">
                        Edit Document
                      </ContextMenuItem>

                      <!-- Delete Document -->
                      <ContextMenuItem
                        class="tw:flex tw:h-6 tw:max-w-60 tw:items-center tw:rounded-sm tw:p-4 tw:text-agency-red tw:outline-none tw:select-none tw:hover:bg-gray-200"
                        @click="handleDeleteModalOpen">
                        Delete Document
                      </ContextMenuItem>

                      <ContextMenuSeparator
                        v-if="(selectedDocument?.accountId && isParentAccount) || (!isClientRole && document.carrierId)"
                        class="tw:my-2 tw:h-px tw:bg-gray-300" />

                      <!-- View Account, if parent account -->
                      <ContextMenuItem v-if="document?.accountId && isParentAccount"
                        class="tw:outline-none tw:max-w-60 tw:hover:bg-gray-200 tw:rounded-sm tw:p-4 tw:flex tw:items-center tw:h-6 tw:select-none"
                        @click="$router.push(`/account/${selectedDocument?.accountId}/overview`)"> View {{ selectedDocument?.accountName }}
                      </ContextMenuItem>

                      <!-- View Carrier -->
                      <ContextMenuItem
                        v-if="document.carrierId && !isClientRole"
                        class="tw:flex tw:h-6 tw:max-w-60 tw:items-center tw:rounded-sm tw:p-4 tw:outline-none tw:select-none tw:hover:bg-gray-200"
                        @click="$router.push(`/carrier/${document.carrierId}/overview`)">
                        View {{ selectedDocument?.carrierName }}
                      </ContextMenuItem>
                    </ContextMenuContent>
                  </ContextMenuPortal>
                </ContextMenuRoot>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <div
      class="tw:drawer-side tw:overflow-hidden"
      :class="{
        'tw:top-(--v-layout-top) tw:max-w-80 tw:border-r tw:pt-0': sideBar.pinned,
        'tw:h-fit tw:pt-(--v-layout-top)': !sideBar.pinned,
      }">
      <label
        for="drawer-toggle"
        aria-label="close sidebar"
        class="tw:drawer-overlay"></label>
      <aside
        class="tw:flex tw:h-screen tw:max-w-60 tw:min-w-60 tw:flex-col tw:overflow-y-auto tw:border-r tw:border-gray-300 tw:bg-gray-100 tw:p-4 tw:pb-20"
        :class="{ 'tw:w-full': sideBar.pinned, 'tw:w-80': !sideBar.pinned }">
        <nav class="tw:sticky tw:top-0">
          <div class="tw:mb-4 tw:flex tw:items-center tw:justify-between">
            <div class="tw:flex tw:items-center tw:gap-2">
              <h2 class="tw:text-sm tw:font-semibold tw:tracking-wide tw:text-gray-700 tw:uppercase">Filters</h2>
              <button
                v-if="hasFiltersSelected"
                class="tw:btn tw:rounded-lg tw:bg-agency-red/70 tw:text-white tw:btn-xs"
                @click="clearFilters">
                Clear All
              </button>
            </div>
            <div class="tw:flex tw:gap-2">
              <label
                class="tw:btn tw:btn-square tw:drop-shadow-sm tw:btn-sm"
                :class="{ 'tw:bg-secondary tw:text-white': sideBar.pinned }"
                @click="toggleSideBarPin">
                <PinIcon :size="16" />
              </label>
              <label
                v-if="!sideBar.pinned"
                for="drawer-toggle"
                class="tw:btn tw:btn-square tw:drop-shadow-sm tw:btn-sm">
                <X :size="16" />
              </label>
            </div>
          </div>

          <AccordionRoot type="multiple" collapsible class="tw:flex tw:flex-col tw:gap-4">
            <!-- Domain Filter (Category) -->
            <AccordionItem value="domainFilters" v-if="sideBar.filterGroups.find((g) => g.name === 'domain')">
              <AccordionHeader>
                <AccordionTrigger class="tw:group tw:mb-1.5 tw:flex tw:w-full tw:items-center tw:justify-between tw:text-left">
                  <div class="tw:flex tw:items-center tw:gap-2">
                    <ChevronRightIcon
                      class="tw:h-3.5 tw:w-3.5 tw:shrink-0 tw:text-gray-500 tw:transition-transform tw:duration-30 tw:group-data-[state=open]:rotate-90" />
                    <h2 class="tw:text-sm tw:font-semibold tw:tracking-wide tw:text-gray-700 tw:uppercase">Category</h2>
                    <span
                      v-if="hasFilteredItems('domain')"
                      class="tw:badge tw:badge-soft tw:badge-sm">
                      {{ sideBar.filterGroups.find((g) => g.name === 'domain')?.options.filter((o) => o.selected).length }}
                    </span>
                    <button
                      v-visible="hasFilteredItems('domain')"
                      class="tw:btn tw:rounded-lg tw:bg-agency-red/70 tw:text-white tw:btn-xs"
                      @click.stop="sideBar.filterGroups.find((g) => g.name === 'domain')?.options.forEach((o) => (o.selected = false))">
                      Clear
                    </button>
                  </div>
                </AccordionTrigger>
              </AccordionHeader>
              <AccordionContent class="tw:overflow-hidden">
                <div class="tw:flex tw:flex-col tw:gap-0.5">
                  <div
                    v-for="(option, i) in sideBar.filterGroups.find((g) => g.name === 'domain')?.options"
                    :key="option.value"
                    :aria-label="option.name"
                    :tabindex="i"
                    role="button"
                    class="tw:flex tw:items-center tw:gap-2 tw:rounded tw:px-2 tw:py-1.5 tw:text-sm tw:transition-all tw:duration-75 tw:hover:bg-gray-200"
                    :class="{
                      'tw:hidden': option.disabled,
                      'tw:bg-specialty-teal/20 tw:font-medium tw:hover:bg-specialty-teal/20': option.selected,
                    }"
                    @click="applyFilter(sideBar.filterGroups.find((g) => g.name === 'domain')!, option)">
                    <FolderIcon class="tw:mt-0.5 tw:h-4 tw:w-4 tw:shrink-0 tw:text-gray-500" />
                    {{ option.name }}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <!-- Document Type Filter -->
            <AccordionItem value="typeFilters" v-if="sideBar.filterGroups.find((g) => g.name === 'document-type')">
              <AccordionHeader>
                <AccordionTrigger class="tw:group tw:mb-1.5 tw:flex tw:w-full tw:items-center tw:justify-between tw:text-left">
                  <div class="tw:flex tw:items-center tw:gap-2">
                    <ChevronRightIcon
                      class="tw:h-3.5 tw:w-3.5 tw:shrink-0 tw:text-gray-500 tw:transition-transform tw:duration-30 tw:group-data-[state=open]:rotate-90" />
                    <h2 class="tw:text-sm tw:font-semibold tw:tracking-wide tw:text-gray-700 tw:uppercase">Type</h2>
                    <span
                      v-if="hasFilteredItems('document-type')"
                      class="tw:badge tw:badge-soft tw:badge-sm">
                      {{ sideBar.filterGroups.find((g) => g.name === 'document-type')?.options.filter((o) => o.selected).length }}
                    </span>
                    <button
                      v-visible="hasFilteredItems('document-type')"
                      class="tw:btn tw:rounded-lg tw:bg-agency-red/70 tw:text-white tw:btn-xs"
                      @click.stop="sideBar.filterGroups.find((g) => g.name === 'document-type')?.options.forEach((o) => (o.selected = false))">
                      Clear
                    </button>
                  </div>
                </AccordionTrigger>
              </AccordionHeader>
              <AccordionContent class="tw:overflow-hidden">
                <div class="tw:flex tw:flex-col tw:gap-0.5">
                  <div
                    v-for="(option, i) in sideBar.filterGroups.find((g) => g.name === 'document-type')?.options"
                    :key="option.value"
                    :aria-label="option.name"
                    :tabindex="i"
                    role="button"
                    class="tw:flex tw:items-center tw:gap-2 tw:rounded tw:px-2 tw:py-1.5 tw:text-sm tw:transition-all tw:duration-75 tw:hover:bg-gray-200"
                    :class="{
                      'tw:hidden': option.disabled,
                      'tw:bg-specialty-teal/20 tw:font-medium tw:hover:bg-specialty-teal/20': option.selected,
                    }"
                    @click="applyFilter(sideBar.filterGroups.find((g) => g.name === 'document-type')!, option)">
                    <FileTextIcon class="tw:mt-0.5 tw:h-4 tw:w-4 tw:shrink-0 tw:text-gray-500" />
                    {{ option.name }}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <!-- Year Filter -->
            <AccordionItem value="yearFilters" v-if="sideBar.filterGroups.find((g) => g.name === 'year')">
              <AccordionHeader>
                <AccordionTrigger class="tw:group tw:mb-1.5 tw:flex tw:w-full tw:items-center tw:justify-between tw:text-left">
                  <div class="tw:flex tw:items-center tw:gap-2">
                    <ChevronRightIcon
                      class="tw:h-3.5 tw:w-3.5 tw:shrink-0 tw:text-gray-500 tw:transition-transform tw:duration-30 tw:group-data-[state=open]:rotate-90" />
                    <h2 class="tw:text-sm tw:font-semibold tw:tracking-wide tw:text-gray-700 tw:uppercase">Year</h2>
                    <span
                      v-if="hasFilteredItems('year')"
                      class="tw:badge tw:badge-soft tw:badge-sm">
                      {{ sideBar.filterGroups.find((g) => g.name === 'year')?.options.filter((o) => o.selected).length }}
                    </span>
                    <button
                      v-visible="hasFilteredItems('year')"
                      class="tw:btn tw:rounded-lg tw:bg-agency-red/70 tw:text-white tw:btn-xs"
                      @click.stop="sideBar.filterGroups.find((g) => g.name === 'year')?.options.forEach((o) => (o.selected = false))">
                      Clear
                    </button>
                  </div>
                </AccordionTrigger>
              </AccordionHeader>
              <AccordionContent class="tw:overflow-hidden">
                <div class="tw:flex tw:flex-col tw:gap-0.5">
                  <div
                    v-for="(option, i) in sideBar.filterGroups.find((g) => g.name === 'year')?.options"
                    :key="option.value"
                    :aria-label="option.name"
                    :tabindex="i"
                    role="button"
                    class="tw:flex tw:items-center tw:gap-2 tw:rounded tw:px-2 tw:py-1.5 tw:text-sm tw:transition-all tw:duration-75 tw:hover:bg-gray-200"
                    :class="{
                      'tw:bg-specialty-teal/20 tw:font-medium tw:hover:bg-specialty-teal/20': option.selected,
                    }"
                    @click="applyFilter(sideBar.filterGroups.find((g) => g.name === 'year')!, option)">
                    <CalendarIcon class="tw:mt-0.5 tw:h-4 tw:w-4 tw:shrink-0 tw:text-gray-500" />
                    {{ option.name }}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <!-- Coverage Type Filter -->
            <AccordionItem value="coverageFilters" v-if="sideBar.filterGroups.find((g) => g.name === 'coverage-type')">
              <AccordionHeader>
                <AccordionTrigger class="tw:group tw:mb-1.5 tw:flex tw:w-full tw:items-center tw:justify-between tw:text-left">
                  <div class="tw:flex tw:items-center tw:gap-2">
                    <ChevronRightIcon
                      class="tw:h-3.5 tw:w-3.5 tw:shrink-0 tw:text-gray-500 tw:transition-transform tw:duration-30 tw:group-data-[state=open]:rotate-90" />
                    <h2 class="tw:text-sm tw:font-semibold tw:tracking-wide tw:text-gray-700 tw:uppercase">Coverage Type</h2>
                    <span
                      v-if="hasFilteredItems('coverage-type')"
                      class="tw:badge tw:badge-soft tw:badge-sm">
                      {{ sideBar.filterGroups.find((g) => g.name === 'coverage-type')?.options.filter((o) => o.selected).length }}
                    </span>
                    <button
                      v-visible="hasFilteredItems('coverage-type')"
                      class="tw:btn tw:rounded-lg tw:bg-agency-red/70 tw:text-white tw:btn-xs"
                      @click.stop="sideBar.filterGroups.find((g) => g.name === 'coverage-type')?.options.forEach((o) => (o.selected = false))">
                      Clear
                    </button>
                  </div>
                </AccordionTrigger>
              </AccordionHeader>
              <AccordionContent class="tw:overflow-hidden">
                <div class="tw:flex tw:flex-col tw:gap-0.5">
                  <div
                    v-for="(option, i) in sideBar.filterGroups.find((g) => g.name === 'coverage-type')?.options"
                    :key="option.value"
                    :aria-label="option.name"
                    :tabindex="i"
                    role="button"
                    class="tw:flex tw:items-center tw:gap-2 tw:rounded tw:px-2 tw:py-1.5 tw:text-sm tw:transition-all tw:duration-75 tw:hover:bg-gray-200"
                    :class="{
                      'tw:bg-specialty-teal/20 tw:font-medium tw:hover:bg-specialty-teal/20': option.selected,
                    }"
                    @click="applyFilter(sideBar.filterGroups.find((g) => g.name === 'coverage-type')!, option)">
                    <ShieldIcon class="tw:mt-0.5 tw:h-4 tw:w-4 tw:shrink-0 tw:text-gray-500" />
                    {{ option.name }}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <!-- Carrier Filter -->
            <AccordionItem value="carrierFilters" v-if="sideBar.filterGroups.find((g) => g.name === 'carrier')">
              <AccordionHeader>
                <AccordionTrigger class="tw:group tw:mb-1.5 tw:flex tw:w-full tw:items-center tw:justify-between tw:text-left">
                  <div class="tw:flex tw:items-center tw:gap-2">
                    <ChevronRightIcon
                      class="tw:h-3.5 tw:w-3.5 tw:shrink-0 tw:text-gray-500 tw:transition-transform tw:duration-30 tw:group-data-[state=open]:rotate-90" />
                    <h2 class="tw:text-sm tw:font-semibold tw:tracking-wide tw:text-gray-700 tw:uppercase">Carrier</h2>
                    <span
                      v-if="hasFilteredItems('carrier')"
                      class="tw:badge tw:badge-soft tw:badge-sm">
                      {{ sideBar.filterGroups.find((g) => g.name === 'carrier')?.options.filter((o) => o.selected).length }}
                    </span>
                    <button
                      v-visible="hasFilteredItems('carrier')"
                      class="tw:btn tw:rounded-lg tw:bg-agency-red/70 tw:text-white tw:btn-xs"
                      @click.stop="sideBar.filterGroups.find((g) => g.name === 'carrier')?.options.forEach((o) => (o.selected = false))">
                      Clear
                    </button>
                  </div>
                </AccordionTrigger>
              </AccordionHeader>
              <AccordionContent class="tw:overflow-hidden">
                <div class="tw:flex tw:flex-col tw:gap-0.5">
                  <div
                    v-for="(option, i) in sideBar.filterGroups.find((g) => g.name === 'carrier')?.options"
                    :key="option.value"
                    :aria-label="option.name"
                    :tabindex="i"
                    role="button"
                    class="tw:flex tw:items-center tw:gap-2 tw:rounded tw:px-2 tw:py-1.5 tw:text-sm tw:transition-all tw:duration-75 tw:hover:bg-gray-200"
                    :class="{
                      'tw:bg-specialty-teal/20 tw:font-medium tw:hover:bg-specialty-teal/20': option.selected,
                    }"
                    @click="applyFilter(sideBar.filterGroups.find((g) => g.name === 'carrier')!, option)">
                    <BuildingIcon class="tw:mt-0.5 tw:h-4 tw:w-4 tw:shrink-0 tw:text-gray-500" />
                    {{ option.name }}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <!-- Tags Filter -->
            <AccordionItem value="tagFilters" v-if="sideBar.filterGroups.find((g) => g.name === 'tags')">
              <AccordionHeader>
                <AccordionTrigger class="tw:group tw:flex tw:w-full tw:items-center tw:justify-between tw:text-left">
                  <div class="tw:flex tw:items-center tw:gap-2">
                    <ChevronRightIcon
                      class="tw:h-3.5 tw:w-3.5 tw:shrink-0 tw:text-gray-500 tw:transition-transform tw:duration-30 tw:group-data-[state=open]:rotate-90" />
                    <h2 class="tw:text-sm tw:font-semibold tw:tracking-wide tw:text-gray-700 tw:uppercase">Tags</h2>
                    <span
                      v-if="hasFilteredItems('tags')"
                      class="tw:badge tw:badge-soft tw:badge-sm">
                      {{ sideBar.filterGroups.find((g) => g.name === 'tags')?.options.filter((o) => o.selected).length }}
                    </span>
                    <button
                      v-visible="hasFilteredItems('tags')"
                      class="tw:btn tw:rounded-lg tw:bg-agency-red/70 tw:text-white tw:btn-xs"
                      @click.stop="sideBar.filterGroups.find((g) => g.name === 'tags')?.options.forEach((o) => (o.selected = false))">
                      Clear
                    </button>
                  </div>
                </AccordionTrigger>
              </AccordionHeader>
              <AccordionContent class="tw:overflow-hidden">
                <div class="tw:flex tw:flex-col tw:gap-0.5">
                  <div
                    v-for="(option, i) in sideBar.filterGroups.find((g) => g.name === 'tags')?.options"
                    :key="option.value"
                    :aria-label="option.name"
                    :tabindex="i"
                    role="button"
                    class="tw:flex tw:items-center tw:gap-2 tw:rounded tw:px-2 tw:py-1.5 tw:text-sm tw:transition-all tw:duration-75 tw:hover:bg-gray-200"
                    :class="{
                      'tw:bg-specialty-teal/20 tw:font-medium tw:hover:bg-specialty-teal/20': option.selected,
                    }"
                    @click="applyFilter(sideBar.filterGroups.find((g) => g.name === 'tags')!, option)">
                    <TagIcon class="tw:mt-0.5 tw:h-4 tw:w-4 tw:shrink-0 tw:text-gray-500" />
                    {{ option.name }}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </AccordionRoot>
        </nav>
      </aside>
    </div>

    <DocumentViewerModal
      :show="showDocumentModal"
      :fileName="selectedDocument?.fileName ?? 'undefined'"
      :fetchDocument="fetchDocument"
      @update:show="showDocumentModal = $event"
      @close="closeDocumentModal" />

    <DocumentEditModal
      v-if="!isClientRole && selectedDocument"
      ref="editDocumentModal"
      title="Edit Document"
      :document="selectedDocument"
      :policy-document-types="allDocumentTypes.filter((t) => t.domainName === selectedDocument?.documentDomainName)"
      :show="showDocumentEditModal"
      :include-tags="true"
      @close="closeDocumentEditModal"
      @update="(request) => updateDocumentInfo(<DocumentWithOtherData>selectedDocument, request)" />

    <AppModal
      v-if="!isClientRole && selectedDocument"
      title="Delete Document"
      :show="showDeleteModal"
      :handle-cancel="handleDeleteModalClose"
      :handle-action="async () => await deleteSelectedDocument()"
      action-button-label="Delete"
      size="lg">
      <template #content>
        <div>Are you sure you want to delete:</div>

        <div class="tw:mt-4 tw:ml-4 tw:font-bold">{{ selectedDocument?.displayName ?? selectedDocument?.fileName }}?</div>

        <div class="tw:mt-4">This cannot be undone.</div>
      </template>
    </AppModal>
  </div>
</template>

<script lang="ts">
import emptySrc from '@/assets/images/filing.svg';
import { accountService } from '@/services/api';
import { Account } from '@/types/Account';
import { documentService } from '@/services/v2/DocumentService';
import { Commit } from '@/store';
import { StoreMutationType } from '@/types/StoreMutationType';
import { DocumentDomainTypeClientUploaded, DocumentType, DocumentWithOtherData, UpdateDocumentRequest } from '@/types/v2/Document';
import { toDateTime, relativeDateTime } from '@/utilities/Dates';
import { DateTime } from 'luxon';
import Skeleton from '@/components/v2/Skeleton.vue';
import AppButton from '@/components/v2/AppButton.vue';
import Spinner from '@/components/v2/Spinner.vue';
import MenuDropdown, { type MenuItem } from '@/components/v2/MenuDropdown.vue';
import Tooltip from '@/components/v2/Tooltip.vue';
import { useToast } from '@/composables/useToast';
import Toggle from '@/components/v2/Toggle.vue';
import DocumentEditModal from '@/components/v2/DocumentEditModal.vue';
import DocumentViewerModal from '@/components/DocumentViewerModal.vue';
import {
  Users,
  X,
  PinIcon,
  XIcon,
  ChevronRightIcon,
  FolderIcon,
  FileTextIcon,
  CalendarIcon,
  ShieldIcon,
  BuildingIcon,
  TagIcon,
  Pencil,
  Download,
  EllipsisVertical,
  Files,
  Trash2,
  Building,
  Briefcase,
  ListFilterIcon,
  Info,
} from 'lucide-vue-next';
import { filters } from '@/utilities/Filters';
import { useUser } from '@/composables/useUser';
import { UserPermission } from '@/types/UserPermissions';
import { UserRole } from '@/types/UserRole';
import Badge from '@/components/Badge.vue';
import {
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionRoot,
  AccordionTrigger,
  ContextMenuRoot,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuPortal,
  ContextMenuSeparator,
} from 'reka-ui';
import AppModal from '@/components/v2/AppModal.vue';

enum validScenarios {
  Account = 'account',
  ParentAccount = 'parentAccount',
}

interface DocumentWithSelection extends DocumentWithOtherData {
  selected: boolean;
}

interface SideBarState {
  pinned: boolean;
  filterGroups: FilterGroup[];
}

interface FilterGroup {
  name: string;
  displayName: string;
  type?: string;
  domain?: string;
  options: FilterOption[];
  grouped?: boolean;
}

interface FilterOption {
  name: string;
  type?: string;
  value: string;
  selected: boolean;
  disabled: boolean; // if the option doesn't exist in the document list
}

interface DocType {
  name: string;
  domain: string;
}

export default {
  name: 'DocumentList',
  props: {
    scenario: {
      type: String,
      validator(value: validScenarios) {
        return [validScenarios.Account, validScenarios.ParentAccount].includes(value);
      },
    },
  },
  setup() {
    const user = useUser();
    const toast = useToast();

    return {
      user,
      toast,
    };
  },
  data: () => ({
    loading: true,
    emptySrc,
    account: {} as Account,
    allDocuments: [] as DocumentWithSelection[],
    showRelativeTime: true,
    showDropArea: false,
    allDocumentTypes: [] as DocumentType[],
    showCurrentOnly: false,
    selectionMode: false,
    selectAll: false,
    isSavingPublishStatus: false,
    errorMessage: '',
    selectedDocTypes: [] as (string | number)[],
    sideBar: {
      pinned: true,
      filterGroups: [],
    } as SideBarState,
    searchFilter: '',
    searchTimeout: null as NodeJS.Timeout | null,
    searchText: '',
    filters,
    DocumentDomainTypeClientUploaded,
    UserPermission,
    showDocumentModal: false,
    showDocumentEditModal: false,
    showDeleteModal: false,
    selectedDocument: null as DocumentWithOtherData | null,
    outsideClicked: false,
  }),
  components: {
    Badge,
    Users,
    X,
    PinIcon,
    DocumentEditModal,
    DocumentViewerModal,
    Toggle,
    Skeleton,
    AppButton,
    Spinner,
    MenuDropdown,
    Tooltip,
    XIcon,
    ChevronRightIcon,
    FolderIcon,
    FileTextIcon,
    CalendarIcon,
    ShieldIcon,
    BuildingIcon,
    TagIcon,
    EllipsisVertical,
    AccordionContent,
    AccordionHeader,
    AccordionItem,
    AccordionRoot,
    AccordionTrigger,
    ContextMenuRoot,
    ContextMenuTrigger,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuPortal,
    ContextMenuSeparator,
    AppModal,
    ListFilterIcon,
    Info,
  },
  computed: {
    hasFiltersSelected() {
      return this.sideBar.filterGroups.some((fg) => fg.options.some((o) => o.selected));
    },
    documentCarriers() {
      return Array.from(
        this.documents.reduce((acc, d) => {
          if (d.carrierName) {
            acc.add(d.carrierName);
          }
          return acc;
        }, new Set<string>()),
      ).sort();
    },
    documentCoverageTypes() {
      return Array.from(
        this.documents.reduce((acc, d) => {
          if (d.coverageType) {
            acc.add(d.coverageType);
          }
          return acc;
        }, new Set<string>()),
      ).sort();
    },
    documentYears() {
      return Array.from(
        this.documents.reduce((acc, d) => {
          if (d.policyEffectiveYear) {
            acc.add(d.policyEffectiveYear.toString());
          }
          return acc;
        }, new Set<string>()),
      ).sort();
    },
    documentDomains() {
      return Array.from(
        this.documents.reduce((acc, d) => {
          if (d.documentDomainName) {
            acc.add(d.documentDomainName);
          }
          return acc;
        }, new Set<string>()),
      );
    },
    documentTags() {
      return Array.from(
        this.allDocuments.reduce((acc, d) => {
          if (d.tags && d.tags.length > 0) {
            d.tags.forEach((tag) => acc.add(tag));
          }
          return acc;
        }, new Set<string>()),
      ).sort();
    },
    documentTypes() {
      const domainFilterGroup = this.sideBar.filterGroups.find((g) => g.name === 'domain');
      const accountTypeOption = domainFilterGroup?.options.find((o) => o.value === 'account');
      const policyTypeOption = domainFilterGroup?.options.find((o) => o.value === 'policy');
      const clientTypeOption = domainFilterGroup?.options.find((o) => o.value === 'client');

      return Array.from(
        this.documents
          .reduce((acc, d) => {
            if (
              (d.documentTypeName &&
                ((accountTypeOption?.selected && d.documentDomainName === 'Account') ||
                  (clientTypeOption?.selected && d.documentDomainName === 'Client') ||
                  (policyTypeOption?.selected && d.documentDomainName === 'Policy'))) ||
              (!accountTypeOption?.selected && !policyTypeOption?.selected && !clientTypeOption?.selected)
            ) {
              acc.set(d.documentTypeName, {
                name: d.documentTypeName,
                domain: d.documentDomainName,
              });
            }
            return acc;
          }, new Map<string, DocType>())
          .values(),
      ).sort();
    },
    DateTime() {
      return DateTime;
    },
    isParentAccount(): boolean {
      return this.scenario === validScenarios.ParentAccount;
    },
    accountId(): string {
      return this.$route.params.id as string;
    },
    uploadEnabled(): boolean {
      return this.scenario !== validScenarios.ParentAccount;
    },
    isClientRole() {
      return this.user.role.value === UserRole.Client;
    },
    isBrokerRole() {
      return this.user.role.value === UserRole.Broker;
    },
    allSelected() {
      const visibleDocs =
        this.selectedDocTypes.length > 0
          ? this.allDocuments.filter((d) => this.selectedDocTypes.some((t) => t === d.documentTypeId))
          : this.allDocuments;
      return visibleDocs.reduce((acc, d) => acc && d.selected, true);
    },
    filteredDocuments(): DocumentWithSelection[] {
      let docs = this.allDocuments;

      if (this.showCurrentOnly) {
        docs = docs.filter(
          (d) =>
            !d.policyExpirationDate ||
            (d.policyExpirationDate && new Date(d.policyExpirationDate).getTime() > new Date().getTime()),
        );
      }

      // Document Domain
      const domainFilterGroup = this.sideBar.filterGroups.find((g) => g.name === 'domain');
      if (domainFilterGroup) {
        const selectedTypes = domainFilterGroup.options.filter((o) => o.selected).map((o) => o.value.toLowerCase());
        if (selectedTypes.length > 0) {
          docs = docs.filter((d) => selectedTypes.includes(d.documentDomainName?.toLowerCase()));
        }
      }

      // Document Type
      const documentTypeFilterGroup = this.sideBar.filterGroups.find((g) => g.name === 'document-type');
      if (documentTypeFilterGroup) {
        const selectedDocumentTypes = documentTypeFilterGroup.options.filter((o) => o.selected).map((o) => o.value);
        if (selectedDocumentTypes.length > 0) {
          docs = docs.filter((d) => selectedDocumentTypes.includes(d.documentTypeName));
        }
      }

      // Year
      const yearFilterGroup = this.sideBar.filterGroups.find((g) => g.name === 'year');
      if (yearFilterGroup) {
        const selectedYears = yearFilterGroup.options.filter((o) => o.selected).map((o) => o.value);
        if (selectedYears.length > 0) {
          docs = docs.filter((d) => selectedYears.includes(d.policyEffectiveYear?.toString()));
        }
      }

      // Coverage Type
      const coverageTypeFilterGroup = this.sideBar.filterGroups.find((g) => g.name === 'coverage-type');
      if (coverageTypeFilterGroup) {
        const selectedCoverageTypes = coverageTypeFilterGroup.options.filter((o) => o.selected).map((o) => o.value);
        if (selectedCoverageTypes.length > 0) {
          docs = docs.filter((d) => selectedCoverageTypes.includes(d.coverageType));
        }
      }

      // Carrier
      const carrierFilterGroup = this.sideBar.filterGroups.find((g) => g.name === 'carrier');
      if (carrierFilterGroup) {
        const selectedCarriers = carrierFilterGroup.options.filter((o) => o.selected).map((o) => o.value);
        if (selectedCarriers.length > 0) {
          docs = docs.filter((d) => selectedCarriers.includes(d.carrierName));
        }
      }

      // Tags
      const tagsFilterGroup = this.sideBar.filterGroups.find((g) => g.name === 'tags');
      if (tagsFilterGroup) {
        const selectedTags = tagsFilterGroup.options.filter((o) => o.selected).map((o) => o.value);
        if (selectedTags.length > 0) {
          docs = docs.filter((d) => d.tags && d.tags.some((tag) => selectedTags.includes(tag)));
        }
      }

      if (this.isClientRole) {
        docs = docs.filter((d) => d.published || d.documentDomainName === DocumentDomainTypeClientUploaded);
      }

      return docs;
    },
    documents() {
      return this.filteredDocuments.filter(
        (d) =>
          (d.displayName ?? d.s3Name)?.toLowerCase().includes(this.searchText.toLowerCase()) ||
          (d.policyDescription ?? '').toLowerCase().includes(this.searchText.toLowerCase()) ||
          (d.mainPolicyNumber ?? '').toLowerCase().includes(this.searchText.toLowerCase()) ||
          (d.tags && d.tags.some((tag) => tag.toLowerCase().includes(this.searchText.toLowerCase()))),
      );
    },
    appliedFilterCount() {
      return this.sideBar.filterGroups.reduce((acc, fg) => {
        return acc + fg.options.filter((o) => o.selected).length;
      }, 0);
    },
  },
  methods: {
    hasFilteredItems(name: string) {
      const filteredItems = this.sideBar.filterGroups.find((g) => g.name === name)?.options.filter((o) => o.selected) ?? [];
      return filteredItems.length > 0;
    },
    clearFilters() {
      this.sideBar.filterGroups.forEach((fg) => {
        fg.options.forEach((o) => {
          o.selected = false;
          o.disabled = false;
        });
      });
    },
    applyFilter(filterGroup: FilterGroup, option: FilterOption) {
      option.selected = !option.selected;

      // Allow only one domain type to be selected at a time and filter out document types based on the selected domain type
      if (filterGroup.name === 'type') {
        const documentTypeFilterGroup = this.sideBar.filterGroups.find((fg) => fg.name === 'document-type');
        documentTypeFilterGroup?.options.map((o) => {
          o.disabled = false;
        });

        // clear all selections for all options for the types that are not selected

        filterGroup.options.forEach((o) => {
          if (o.value !== option.value) {
            o.selected = false;
          }
        });

        documentTypeFilterGroup?.options.forEach((o) => {
          if (o.type !== option.name) {
            o.disabled = true;
            o.selected = false;
          }
        });
      }
    },
    toggleSideBarPin() {
      this.sideBar.pinned = !this.sideBar.pinned;
    },
    resolveFilterGroups() {
      // Account / Policy / Client domains
      this.sideBar.filterGroups.push(
        this.documentDomains.reduce(
          (acc, domain) => {
            acc.options.push({
              name: domain,
              value: domain,
              type: domain,
              disabled: false,
              selected: false,
            });
            return acc;
          },
          { name: 'domain', displayName: 'Category', options: [], grouped: true } as FilterGroup,
        ),
      );

      // Account / Policy / Client document types
      this.sideBar.filterGroups.push(
        this.documentTypes.reduce(
          (acc, type) => {
            acc.options.push({
              name: type.name,
              value: type.name,
              type: type.domain,
              disabled: false,
              selected: false,
            });
            return acc;
          },
          { name: 'document-type', displayName: 'Type', options: [] } as FilterGroup,
        ),
      );

      // Year
      this.sideBar.filterGroups.push(
        this.documentYears.reduce(
          (acc, year) => {
            acc.options.push({
              name: year,
              value: year,
              disabled: false,
              selected: false,
            });
            return acc;
          },
          { name: 'year', displayName: 'Year', options: [] } as FilterGroup,
        ),
      );

      // Coverage Type
      this.sideBar.filterGroups.push(
        this.documentCoverageTypes.reduce(
          (acc, coverageType) => {
            acc.options.push({
              name: coverageType,
              value: coverageType,
              disabled: false,
              selected: false,
            });
            return acc;
          },
          { name: 'coverage-type', displayName: 'Coverage Type', options: [] } as FilterGroup,
        ),
      );

      // Carrier
      this.sideBar.filterGroups.push(
        this.documentCarriers.reduce(
          (acc, carrier) => {
            acc.options.push({
              name: carrier,
              value: carrier,
              disabled: false,
              selected: false,
            });
            return acc;
          },
          { name: 'carrier', displayName: 'Carrier', options: [] } as FilterGroup,
        ),
      );

      // Tags
      this.updateTagsFilterGroup();
    },
    updateTagsFilterGroup() {
      // Find existing tags filter group or create new one
      const existingTagsGroup = this.sideBar.filterGroups.find((g) => g.name === 'tags');
      const currentTags = this.documentTags;
      const selectedTags = existingTagsGroup?.options.filter((o) => o.selected).map((o) => o.value) ?? [];

      // Create new tags filter group
      const tagsGroup = currentTags.reduce(
        (acc, tag) => {
          acc.options.push({
            name: tag,
            value: tag,
            disabled: false,
            selected: selectedTags.includes(tag), // Preserve selected state
          });
          return acc;
        },
        { name: 'tags', displayName: 'Tags', options: [] } as FilterGroup,
      );

      // Replace existing tags group or add new one
      if (existingTagsGroup) {
        const index = this.sideBar.filterGroups.indexOf(existingTagsGroup);
        this.sideBar.filterGroups[index] = tagsGroup;
      } else {
        this.sideBar.filterGroups.push(tagsGroup);
      }
    },
    toDateTime,
    relativeDateTime,
    async downloadFile(doc: DocumentWithOtherData) {
      if (!doc.documentId) {
        console.log('No source document key found');
        return;
      }

      if (!doc.accountId) {
        console.log('No account ID found for document: ', doc);
        return;
      }

      try {
        let url = await documentService.downloadDocumentFile(doc.accountId, doc.documentId);
        const anchorElement = document.createElement('a');
        anchorElement.href = url;
        anchorElement.download = doc.displayName ?? doc.fileName ?? 'download';
        anchorElement.setAttribute('target', '_blank');
        anchorElement.click();
        anchorElement.remove();
      } catch (err) {
        console.error(err);
      }
    },
    async fetchDocument(convertToPdf: boolean): Promise<string> {
      if (!this.selectedDocument?.documentId || !this.selectedDocument.accountId) {
        return '';
      }

      try {
        return await documentService.downloadDocumentFile(
          this.selectedDocument.accountId,
          this.selectedDocument.documentId,
          convertToPdf,
        );
      } catch (error) {
        console.error('Error loading document:', error);
      }

      return '';
    },
    async deleteDocument(file: DocumentWithOtherData): Promise<boolean> {
      try {
        await documentService.deleteDocumentByIdAndAccount(Number(this.account.accountID), file.documentId);
        Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Document successfully deleted' });
        await this.refreshData();
      } catch (err) {
        console.error(err);
        Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Document failed to delete', color: 'error' });
      }

      return true;
    },
    async refreshData() {
      this.loading = true;
      const [account, documentTypes, allDocuments] = await Promise.all([
        accountService.findById(this.$route.params.id as string),
        documentService.getDocumentTypes(),
        this.fetchDocuments(),
      ]);

      this.allDocumentTypes = documentTypes.sort((a, b) => a.name.localeCompare(b.name));

      this.account = account;

      this.allDocuments = allDocuments.map((d) => ({ ...d, selected: d.published }));

      this.loading = false;

      return;
    },
    async fetchDocuments(): Promise<DocumentWithOtherData[]> {
      const accountId = Number(this.$route.params.id as string);
      if (this.isParentAccount) {
        return documentService.getDocumentsWithOtherDataUnderParentAccount(accountId);
      } else {
        return documentService.getDocumentsWithOtherDataByAccount(accountId);
      }
    },
    async updateDocumentInfo(document: DocumentWithOtherData, request?: UpdateDocumentRequest) {
      if (request) {
        await documentService.updateDocument(document.documentId, request);
        document.documentTypeId = request.documentTypeId ?? document.documentTypeId;
        document.documentTypeName = this.allDocumentTypes.find((t) => t.id === request.documentTypeId)?.name ?? document.documentTypeName;
        document.displayName = request.displayName ?? document.displayName;
        document.tags = request.tags ?? document.tags;
        // Update filter groups to reflect any tag changes
        this.updateTagsFilterGroup();
        this.closeDocumentEditModal();
      }
    },
    toggleSelectionMode() {
      this.selectionMode = !this.selectionMode;
      this.selectAll = this.documents.reduce((acc, d) => acc && d.selected, true);
    },
    toggleSelectAll() {
      this.documents.forEach((d) => {
        d.selected = this.selectAll;
      });
    },
    async saveSelection() {
      setTimeout(() => {
        this.isSavingPublishStatus = true;
      }, 250);

      const [docsToPublish, docsToUnpublish] = [
        this.documents.filter((d) => !d.published && d.selected),
        this.documents.filter((d) => d.published && !d.selected),
      ];

      try {
        if (docsToPublish.length > 0) {
          const publishPromises = docsToPublish.map((d) => documentService.publishDocument(d.documentId));
          await Promise.all(publishPromises);

          docsToPublish.forEach((d) => {
            d.published = true;
            d.selected = true;
          });
        }

        if (docsToUnpublish.length > 0) {
          const unpublishPromises = docsToUnpublish.map((d) => documentService.unPublishDocument(d.documentId));
          await Promise.all(unpublishPromises);

          docsToUnpublish.forEach((d) => {
            d.published = false;
            d.selected = false;
          });
        }
      } catch (e) {
        console.error(e);
        this.toast.error('An error occurred while trying to save publishing status.');
      }

      this.selectionMode = false;

      setTimeout(() => {
        this.isSavingPublishStatus = false;
      }, 350);
    },
    cancelSelection() {
      this.documents.forEach((d) => {
        d.selected = d.published;
      });
      this.selectionMode = false;
    },
    isDocumentTypeViewable(document: DocumentWithOtherData) {
      return document.fileName.endsWith('.pdf') || document.fileName.endsWith('.docx');
    },
    openDocumentModal(document: DocumentWithOtherData) {
      this.selectedDocument = document;
      this.showDocumentModal = true;
    },
    closeDocumentModal() {
      this.showDocumentModal = false;
      this.selectedDocument = null;
    },
    openDocumentEditModal(document?: DocumentWithOtherData | null) {
      if (!document) return;
      this.selectedDocument = document;
      this.showDocumentEditModal = true;
    },
    closeDocumentEditModal() {
      this.showDocumentEditModal = false;
      this.selectedDocument = null;
    },
    setSelectedDocument(document: DocumentWithOtherData) {
      this.selectedDocument = document;
    },
    viewSelectedDocument() {
      this.showDocumentModal = true;
    },
    downloadSelectedDocument() {
      if (!this.selectedDocument) return;
      this.downloadFile(this.selectedDocument);
    },
    async deleteSelectedDocument(): Promise<boolean> {
      if (!this.selectedDocument) return false;
      return await this.deleteDocument(this.selectedDocument);
    },
    handleDeleteModalOpen() {
      this.showDeleteModal = true;
    },
    handleDeleteModalClose() {
      this.showDeleteModal = false;
    },
    openDeleteModal(doc: DocumentWithOtherData) {
      this.selectedDocument = doc;
      this.showDeleteModal = true;
    },
    getDocumentMenuItems(document: DocumentWithSelection): MenuItem[] {
      const items: MenuItem[] = [];
      if (this.isDocumentTypeViewable(document)) {
        items.push({
          label: 'View Document',
          handler: () => this.openDocumentModal(document),
          icon: Files,
        });
      }
      items.push({
        label: 'Download Document',
        handler: () => this.downloadFile(document),
        icon: Download,
      });
      if (!this.isClientRole) {
        items.push({
          label: 'Edit Document',
          handler: () => this.openDocumentEditModal(document),
          icon: Pencil,
        });
        items.push({
          label: 'Delete Document',
          handler: () => this.openDeleteModal(document),
          icon: Trash2,
          iconColorClass: 'tw:text-error',
        });
      }
      if (document.accountId && this.isParentAccount) {
        items.push({
          label: `View ${document.accountName}`,
          handler: () => this.$router.push(`/account/${document.accountId}/overview`),
          icon: Building,
        });
      }
      if (document.carrierId && !this.isClientRole) {
        items.push({
          label: `View ${document.carrierName}`,
          handler: () => this.$router.push(`/carrier/${document.carrierId}/overview`),
          icon: Briefcase,
        });
      }
      return items;
    },
    formatCreatedByUser(createdByUser: string | undefined): string {
      if (!createdByUser) return '';
      if (createdByUser.toLowerCase().startsWith('platform@')) {
        return 'Epic';
      }
      return createdByUser;
    },
  },
  watch: {
    allSelected(newVal) {
      // if all docs are selected/published and the user deselects one, deselect the "select all" checkbox
      this.selectAll = newVal;
    },
    searchFilter(newVal) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      this.searchTimeout = setTimeout(() => {
        this.searchText = newVal;
      }, 400);
    },
  },
  async created(): Promise<void> {
    await this.refreshData();
    this.resolveFilterGroups();
  },
};
</script>

<style scoped>
td {
  white-space: nowrap;
}

.truncate {
  display: block;
  width: 10rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
